import { useEffect, useRef, type ReactNode, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { getPortalTarget } from '../../utils/shadow-root';

interface ModalProps {
  opened: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full';
  centered?: boolean;
  closeOnClickOutside?: boolean;
  closeOnEscape?: boolean;
  withCloseButton?: boolean;
  className?: string;
}

export function Modal({
  opened,
  onClose,
  title,
  children,
  size = 'md',
  centered = true,
  closeOnClickOutside = true,
  closeOnEscape = true,
  withCloseButton = true,
  className = '',
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  const sizeMap = {
    xs: 'caddietw-max-w-sm',
    sm: 'caddietw-max-w-md',
    md: 'caddietw-max-w-2xl',
    lg: 'caddietw-max-w-4xl',
    xl: 'caddietw-max-w-6xl',
    full: 'caddietw-max-w-[90vw]',
  };

  useEffect(() => {
    if (!opened || !closeOnEscape) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [opened, closeOnEscape, onClose]);

  useEffect(() => {
    if (opened) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [opened]);

  useEffect(() => {
    if (!opened || !modalRef.current) return;

    const focusableElements = modalRef.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[
      focusableElements.length - 1
    ] as HTMLElement;

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement?.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement?.focus();
          e.preventDefault();
        }
      }
    };

    // Prioritize input/textarea for initial focus, otherwise fallback to first element
    const inputElement = modalRef.current.querySelector(
      'input, textarea'
    ) as HTMLElement;
    (inputElement || firstElement)?.focus();

    document.addEventListener('keydown', handleTab);
    return () => document.removeEventListener('keydown', handleTab);
  }, [opened]);

  const handleOverlayClick = (e: MouseEvent<HTMLDivElement>) => {
    if (closeOnClickOutside && e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!opened) return null;

  const modal = (
    <div
      className="caddietw-fixed caddietw-inset-0"
      style={{ zIndex: 9999, pointerEvents: 'auto' }}
    >
      {/* Overlay */}
      <div
        className="caddietw-fixed caddietw-inset-0 caddietw-bg-black/60 caddietw-backdrop-blur-md"
        style={{ zIndex: 9999 }}
        aria-hidden="true"
        onClick={handleOverlayClick}
      />

      {/* Modal Container */}
      <div
        className={`caddietw-fixed caddietw-inset-0 caddietw-overflow-y-auto ${
          centered
            ? 'caddietw-flex caddietw-items-center caddietw-justify-center'
            : ''
        } caddietw-p-4`}
        style={{ zIndex: 10000 }}
        onClick={handleOverlayClick}
      >
        {/* Modal Content */}
        <div
          ref={modalRef}
          className={`caddietw-relative caddietw-w-full ${sizeMap[size]} caddietw-mx-auto ${className}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? 'modal-title' : undefined}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="caddietw-relative caddietw-bg-white caddietw-rounded-xl caddietw-shadow-2xl">
            {/* Header */}
            {(title || withCloseButton) && (
              <div className="caddietw-flex caddietw-items-start caddietw-justify-between caddietw-px-8 caddietw-pt-6 caddietw-pb-4">
                {title && (
                  <div
                    id="modal-title"
                    className="caddietw-text-2xl caddietw-font-bold caddietw-text-gray-900"
                  >
                    {title}
                  </div>
                )}
                {withCloseButton && (
                  <button
                    type="button"
                    onClick={onClose}
                    className="caddietw-ml-auto caddietw--mt-1 caddietw-text-gray-400 hover:caddietw-text-gray-600 caddietw-transition-colors caddietw-flex-shrink-0"
                    aria-label="Close modal"
                  >
                    <svg
                      className="caddietw-w-7 caddietw-h-7"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            )}

            {/* Body */}
            <div className="caddietw-px-8 caddietw-pb-6">{children}</div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, getPortalTarget());
}
